import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ReconOpportunityEntity } from './recon-opportunity.entity';
import { ReconSignalEntity } from './recon-signal.entity';

/**
 * AI-generated draft interactions awaiting approval.
 * Human reviews, optionally edits, then approves or rejects.
 */
@Entity('recon_interactions')
@Index('idx_recon_interactions_status', ['status'])
@Index('idx_recon_interactions_scheduled', ['scheduledFor'])
export class ReconInteractionEntity extends BaseEntity {
	// Links
	@Column('int', { name: 'opportunity_id', unsigned: true })
	public opportunityId!: number;

	@Column('int', { name: 'signal_id', unsigned: true })
	public signalId!: number;

	// Action details
	/**
	 * Type of action.
	 * 'reply', 'dm', 'connection_request', 'post', 'like'
	 */
	@Column('varchar', { name: 'action_type', length: 50 })
	public actionType!: string;

	@Column('varchar', { name: 'target_platform', length: 50 })
	public targetPlatform!: string;

	@Column('varchar', { name: 'target_profile_id', length: 255, nullable: true })
	public targetProfileId!: string | null;

	@Column('varchar', { name: 'target_content_id', length: 500, nullable: true })
	public targetContentId!: string | null;

	// Content
	@Column('text', { name: 'draft_content' })
	public draftContent!: string;

	@Column('text', { name: 'content_strategy', nullable: true })
	public contentStrategy!: string | null;

	@Column('text', { name: 'why_this_action', nullable: true })
	public whyThisAction!: string | null;

	// Approval state
	/**
	 * Processing status.
	 * 'draft', 'approved', 'modified', 'rejected', 'sent', 'failed'
	 */
	@Column('varchar', { name: 'status', length: 30, default: 'draft' })
	public status!: string;

	@Column('int', { name: 'reviewed_by', unsigned: true, nullable: true })
	public reviewedBy!: number | null;

	@Column('datetime', { name: 'reviewed_at', nullable: true })
	public reviewedAt!: Date | null;

	/**
	 * Content after human edits (if modified before approval).
	 */
	@Column('text', { name: 'modified_content', nullable: true })
	public modifiedContent!: string | null;

	@Column('text', { name: 'reject_reason', nullable: true })
	public rejectReason!: string | null;

	// Execution
	/**
	 * Scheduled send time (must be within business hours).
	 */
	@Column('datetime', { name: 'scheduled_for', nullable: true })
	public scheduledFor!: Date | null;

	@Column('datetime', { name: 'sent_at', nullable: true })
	public sentAt!: Date | null;

	/**
	 * ID returned by platform after successful send.
	 */
	@Column('varchar', {
		name: 'platform_response_id',
		length: 500,
		nullable: true,
	})
	public platformResponseId!: string | null;

	@Column('text', { name: 'send_error', nullable: true })
	public sendError!: string | null;

	// Relations
	@ManyToOne(() => ReconOpportunityEntity)
	@JoinColumn({ name: 'opportunity_id' })
	public opportunity?: ReconOpportunityEntity;

	@ManyToOne(() => ReconSignalEntity)
	@JoinColumn({ name: 'signal_id' })
	public signal?: ReconSignalEntity;
}
